import { Fragment, Suspense } from 'react';

import {
  generateModalRoutes,
  generatePreservedRoutes,
  generateRegularRoutes,
} from '@generouted/react-router/core';
import { Outlet, RouterProvider, createBrowserRouter, useLocation } from 'react-router-dom';

import type { ActionFunction, LoaderFunction, RouteObject } from 'react-router-dom';

type Element = () => JSX.Element;
type Module = {
  default: Element;
  Loader?: LoaderFunction;
  Action?: ActionFunction;
  Catch?: Element;
  Pending?: Element;
};

const PRESERVED = import.meta.glob<Module>('/src/pages/(_app|404).{jsx,tsx}', { eager: true });
const MODALS = import.meta.glob<Pick<Module, 'default'>>('/src/pages/**/[+]*.{jsx,tsx}', {
  eager: true,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let ROUTES: any = null;

if (import.meta.env.DEV) {
  ROUTES = import.meta.glob<Module>(
    ['/src/pages/**/[\\w[-]*.{jsx,tsx,mdx}', '!/src/pages/**/(_!(layout)*(/*)?|_app|404)*'],
    { eager: true },
  );
} else {
  ROUTES = import.meta.glob<Module>([
    '/src/pages/**/[\\w[-]*.{jsx,tsx,mdx}',
    '!/src/pages/**/(_!(layout)*(/*)?|_app|404)*',
  ]);
}

const preservedRoutes = generatePreservedRoutes<Omit<Module, 'Action'>>(PRESERVED);
const modalRoutes = generateModalRoutes<Element>(MODALS);

let regularRoutes: RouteObject[] = [];
if (import.meta.env.DEV) {
  regularRoutes = generateRegularRoutes<RouteObject, Partial<Module>>(ROUTES, (module, key) => {
    const index =
      /index\.(jsx|tsx|mdx)$/.test(key) && !key.includes('pages/index') ? { index: true } : {};
    const Default = module?.default || Fragment;
    const Page = () =>
      module?.Pending ? (
        <Suspense fallback={<module.Pending />}>
          <Default />
        </Suspense>
      ) : (
        <Default />
      );
    return {
      ...index,
      Component: Page,
      ErrorBoundary: module?.Catch,
      loader: module?.Loader,
      action: module?.Action,
    };
  });
} else {
  regularRoutes = generateRegularRoutes<RouteObject, () => Promise<Partial<Module>>>(
    ROUTES,
    (module, key) => {
      const index =
        /index\.(jsx|tsx|mdx)$/.test(key) && !key.includes('pages/index') ? { index: true } : {};
      return {
        ...index,
        lazy: async () => {
          const Default = (await module())?.default || Fragment;
          const Pending = (await module())?.Pending;
          const Page = () =>
            Pending ? (
              <Suspense fallback={<Pending />}>
                <Default />
              </Suspense>
            ) : (
              <Default />
            );

          return {
            Component: Page,
            ErrorBoundary: (await module())?.Catch,
            loader: (await module())?.Loader,
            action: (await module())?.Action,
          };
        },
      };
    },
  );
}

const _app = preservedRoutes?.['_app'];
const _404 = preservedRoutes?.['404'];

const Default = _app?.default || Outlet;

const Layout = () => (
  <>
    <Default />
  </>
);

const App = () => (_app?.Pending ? <Suspense fallback={<_app.Pending />}></Suspense> : <Layout />);

const app = {
  Component: _app?.default ? App : Layout,
  ErrorBoundary: _app?.Catch,
  loader: _app?.Loader,
};
const fallback = { path: '*', Component: _404?.default || Fragment };

export const routes: RouteObject[] = [{ ...app, children: [...regularRoutes, fallback] }];
const router = createBrowserRouter(routes);
export const Routes = () => <RouterProvider router={router} />;

export const Modals = () => {
  const Modal = modalRoutes[useLocation().state?.modal] || Fragment;
  return <Modal />;
};
